// ./client/src/EmojiPicker.tsx
import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

// Emoji picker data - organized by category
const emojiCategories = [
  {
    name: 'Expressions',
    emojis: ['😂', '😃', '😁', '😍', '😢', '😮', '😡', '😅', '😎', '😧', '😲', '😩', '💀', '🤫', '🤯', '😵', '🙌', '😇', '🙃', '😜', '🤨', '🥳', '😔', '😐', '😋', '🥴', '🤔', '🙂', '🤑', '😭']
  },
  {
    name: 'Gestures',
    emojis: ['👍', '👎', '👆', '👀', '🙏', '🫂', '💪', '🤝', '🤘', '🤙', '✌️', '💅']
  },
  {
    name: 'Animals',
    emojis: ['🐶', '🐱', '🦕', '🦖', '🐵', '🙈', '🙉', '🙊', '🐧', '🦆', '🦇', '🐌', '🐢', '🦐', '🦘', '🐕', '🦝', '🐇', '🦩', '🦔', '🐀', '🦙', '🦄', '🐴', '🦅', '🦀', '🐛', '🦜', '🦥']
  },
  {
    name: 'Nature',
    emojis: ['🌲', '🌳', '🍄', '🍀', '🌕', '☀️', '🌎', '🌠', '💥', '🌧', '🌩', '🌨', '☔️', '⭐', '🌷', '🔥']
  },
  {
    name: 'Food & Drink',
    emojis: ['🍏', '🍎', '🍋', '🥝', '🥥', '🥨', '🍟', '🌭', '🍕', '🥂', '🍾', '🍸', '🧉', '🧂', '🥄', '🍽', '🍪', '🍉', '🍿', '🍇', '🍊', '🍍', '🍅', '🌶', '🧀']
  },
  {
    name: 'Activities',
    emojis: ['⚽️', '🏀', '🏈', '⚾️', '🥎', '🏒', '🥅', '🥇', '🥈', '🥉', '🏆', '🎲', '🎯', '🎳', '🎮', '🎰', '🧩', '🧶', '🧵', '🎱', '♟']
  },
  {
    name: 'Entertainment',
    emojis: ['🎬', '🎨', '🎤', '🎧', '🎹', '🥁', '🎷', '🎺', '🎸', '🪕', '🎻', '🎉', '🎊', '🎶', '🎵', '📽', '🎭']
  },
  {
    name: 'Travel & Objects',
    emojis: ['🚗', '🚒', '🚑', '🛸', '🎢', '🏝', '🏜', '🌋', '⛰', '💡', '💰', '🔮', '🔑', '👓', '👑', '🎅', '📻', '📢', '🛒', '✈', '🛫', '⛵', '🧯']
  },
  {
    name: 'Hearts & Symbols',
    emojis: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '☮️', '❌', '❕', '❔', '✅', '⚖️', '🧊', '💯']
  }
];


interface EmojiPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectEmoji: (emoji: string) => void;
  position: { top: number; left: number };
  userReactions: Set<string>;
}

const EmojiPicker: React.FC<EmojiPickerProps> = ({
  isOpen,
  onClose,
  onSelectEmoji,
  position,
  userReactions,
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={popoverRef}
      className="fixed bg-gray-700 border border-gray-600 rounded-lg shadow-xl z-[1001]" // Higher z-index than modal
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: '320px',
        maxHeight: '400px',
      }}
      onClick={(e) => e.stopPropagation()} 
    >
      <div className="p-3">
        {/* MODIFIED LINE BELOW: Added px-1 for alignment */}
        <div className="flex justify-between items-center mb-2 px-1">
          <h3 className="text-xs text-gray-400">Add reaction</h3>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-800 transition-colors"
            title="Close"
          >
            <X size={16} className="text-gray-400 hover:text-white" />
          </button>
        </div>
        <div className="h-80 overflow-y-auto px-1 custom-scrollbar-emoji-picker">
          {emojiCategories.map(category => (
            <div key={category.name} className="mb-3">
              <h4 className="text-xs text-gray-500 mb-1 font-medium uppercase">{category.name}</h4>
              <div className="grid grid-cols-6 gap-1.5">
                {category.emojis.map(emoji => (
                  <button
                    key={emoji}
                    onClick={() => onSelectEmoji(emoji)}
                    className={`w-9 h-9 flex items-center justify-center rounded transition-all text-lg -m-0.5 ${
                      userReactions.has(emoji)
                        ? 'bg-purple-600/30 text-purple-300 scale-105 ring-1 ring-purple-400' 
                        : 'hover:bg-gray-800 hover:scale-110' 
                    }`}
                    title={emoji}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <style jsx>{`
        .custom-scrollbar-emoji-picker::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar-emoji-picker::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.1);
          border-radius: 2px;
        }
        .custom-scrollbar-emoji-picker::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 2px;
        }
        .custom-scrollbar-emoji-picker::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.2);
        }
      `}</style>
    </div>
  );
};

export default EmojiPicker;
